import { Mutation, Query } from 'react-apollo'
import React, { useEffect, useState } from 'react'
import gql from 'graphql-tag'
import CardLayout from 'components/CardLayout'
import Form from 'react-jsonschema-form'
import LayoutField from 'react-jsonschema-form-layout-grid'
import { compose, evolve, map } from 'ramda'
import { injectIntl } from 'react-intl'
import { LoadingView } from '../Loading/Loading'
import IntlMessages, { toParagraphs } from 'util/IntlMessages'
import { checkProfileStatus } from 'actions'
import { connect } from 'react-redux'
import { checkCard, useStripe } from 'util/Stripe'
import cx from 'classnames'

import { debug as dbg } from 'debug'
import { updateLocaleSetting } from './settings'

const debug = dbg('linkgua:customer:details')

const customerFields = gql`
  fragment customerFields on Customer {
    id
    email
    firstName
    lastName
    phone
    companyName
    vatId: taxId
    address1
    address2
    zip
    city
    area
    country
    creditCard {
      last4
      expMonth
      expYear
    }
    profileStatus
    vatAndCountryLocked
    billingMethod
  }`

const query = gql`
  ${customerFields}
  query {
    countryCodes

    customer {
      ...customerFields
    }
  }`

export const create = gql`
  ${customerFields}


  mutation AddCustomer($firstName: String, $lastName: String, $phone: String, $companyName: String, $vatId: String, $address1: String, $address2: String, $zip: String, $city: String, $area: String, $country: String){
    addCustomer(newCustomer: {
      firstName: $firstName,
      lastName: $lastName,
      phone: $phone,
      companyName: $companyName,
      taxId: $vatId,
      address1: $address1,
      address2: $address2,
      zip: $zip,
      city: $city,
      area: $area,
      country: $country
    }) {
      ...customerFields
    }
  }`

export const update = gql`
  ${customerFields}

  mutation ($firstName: String, $lastName: String, $phone: String, $companyName: String, $vatId: String, $address1: String, $address2: String, $zip: String, $city: String, $area: String, $country: String){
    updateCustomer(customer: {
      firstName: $firstName,
      lastName: $lastName,
      phone: $phone,
      companyName: $companyName,
      taxId: $vatId,
      address1: $address1,
      address2: $address2,
      zip: $zip,
      city: $city,
      area: $area,
      country: $country,
    }) {
      ...customerFields        }
  }`


const getSchema = (intl, countries, billingMethod) => {
  const Optional = ({children}) => <React.Fragment><IntlMessages id={`component.customerDetails.${children}`}/>
    <em> - <IntlMessages id={'component.customerDetails.fieldOptional'}/></em></React.Fragment>
  const Required = ({children}) => <b><IntlMessages id={`component.customerDetails.${children}`}/></b>

  const mapEnumNames = (prefix) => map(value => intl.formatMessage({id: `${prefix}.${value}`}))

  const configuration = {
    type: 'object',
    properties: {
      firstName: {type: 'string', title: <Required>firstName</Required>},
      lastName: {type: 'string', title: <Required>lastName</Required>},
      email: {type: 'string', title: <Required>email</Required>},
      phone: {type: 'string', title: <Optional>phone</Optional>},
      companyName: {type: 'string', title: <Required>companyName</Required>},
      vatId: {type: 'string', title: <Required>vatId</Required>},
      address1: {type: 'string', title: <Required>address1</Required>},
      address2: {type: 'string', title: <Optional>address2</Optional>},
      zip: {type: 'string', title: <Required>zip</Required>},
      city: {type: 'string', title: <Required>city</Required>},
      area: {type: 'string', title: <Optional>area</Optional>},
      country: {
        type: 'string',
        title: <Required>country</Required>,
        enum: countries,
        enumNames: mapEnumNames('country')(countries)
      }
    }
  }

  if(billingMethod === 'sendInvoice') {
    configuration.properties.invoice = {type: 'string', title: <Required>paymentMethod</Required>}
  } else {
    configuration.properties.creditCard = {type: 'string', title: <Required>creditCard</Required>}
  }

  return configuration
}


const stripeWidget = (intl, Stripe) => (props) => {
  const defaultMessage = 'component.customerDetails.creditCardMessage'
  const [creditCard, setCreditCard] = useState({ cardMessage:  defaultMessage});
  const cardContainer = React.createRef()
  const formatCreditCardMessage = (card) => intl.formatMessage({id: 'component.customerDetails.cardDetails'}, JSON.parse(card))


  useEffect(() => {
    if (props.value) {
      cardContainer.current.textContent = formatCreditCardMessage(props.value)
      setCreditCard(() => ({ ...creditCard, cardInput: formatCreditCardMessage(props.value) }));
    } else {
      const stripe = Stripe(process.env.STRIPE_PUBLIC_KEY);
      const elements = stripe.elements();
      const card = elements.create('card', {hidePostalCode: true})
      card.mount(cardContainer.current)
      card.on('change', checkCard(
        stripe,
        card,
        (cardValues) => {
          const values = JSON.stringify(cardValues)
          setCreditCard(() =>  ({ ...creditCard, cardMessage: formatCreditCardMessage(values) }));
          cardContainer.current.textContent = formatCreditCardMessage(values);
          props.onChange(values)
        },
        (message) => {
          setCreditCard(() => ({ ...creditCard, cardMessage: message }));
        },
        {
          query: query
        }))
    }
  }, [props.value])


  return (
    <div>
      <div ref={cardContainer}/>
      <em><IntlMessages id={creditCard.cardMessage}/></em>
    </div>
  )
}

const getUiSchema = (intl, Stripe, isTaxAndCountryLocked, isVatIdInvalid) => ({
  'ui:field': 'layout_grid',
  'ui:layout_grid': {
    'ui:row': [
      {
        'ui:col': {
          md: 12,
          children: [
            {
              'ui:group': intl.formatMessage({id: 'component.customerDetails.formTitle'}), 'ui:row': [
                {'ui:col': {md: 7, children: ['firstName']}},
                {'ui:col': {md: 5, children: ['email']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 12, children: ['lastName']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 12, children: ['companyName']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 12, children: ['address1']}},
                {'ui:col': {md: 12, children: ['address2']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 3, children: ['zip']}},
                {'ui:col': {md: 9, children: ['city']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 3, children: ['area']}},
                {'ui:col': {md: 9, children: ['country']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 7, children: ['phone']}},
                {'ui:col': {md: 5, children: ['vatId']}}
              ]
            },
            {
              'ui:row': [
                {'ui:col': {md: 12, children: ['creditCard']}},
                {'ui:col': {md: 12, children: ['invoice']}}
              ]
            }
          ]
        }
      }
    ]
  },
  email: {
    'ui:disabled': true,
    'ui:options': {
      'inputType': 'email'
    }
  },
  phone: {
    'ui:options': {
      'inputType': 'tel'
    }
  },
  creditCard: {
    'ui:widget': stripeWidget(intl, Stripe),
  },
  invoice: {
    'ui:widget': () => <div><em><IntlMessages id="component.customerDetails.paymentMethod.sendInvoice"/></em></div>
  },
  country: {
    'ui:disabled': isTaxAndCountryLocked
  },
  vatId: {
    'ui:disabled': isTaxAndCountryLocked,
    'ui:placeholder': intl.formatMessage({id: 'component.customerDetails.vatIdMessage'}),
    'ui:help': isVatIdInvalid ? <span className="text-danger icons-wrapper">Not valid</span> : undefined
  }
})


const Error = ({error}) => error.graphQLErrors.size > 1
  ? error.graphQLErrors.map(e => `${e.extensions.code}: ${e.message}`).join('<br />')
  : error.message

const makeNullsUndefined = map(value => value === null ? undefined : value)


const formatIncoming = compose(
  evolve({
    creditCard: JSON.stringify
  }),
  makeNullsUndefined
)

const ProfileStatus = ({customer}) => {

  const readyForOrdering = customer.profileStatus !== 'initiated'

  return <div className="media social-list-line">
    <div
      className={`border-${readyForOrdering ? 'success' : 'gray'} bg-white icon-btn user-avatar size-40 z-index-20 align-item-self mr-3`}>
      <i
        className={`zmdi zmdi-check-circle text-${readyForOrdering ? 'success' : 'gray'} zmdi-hc-fw`}></i>
    </div>
    <div className="media-body">
      <h4 className="mt-2"><IntlMessages
        id={`pages.customer.profile.status.${customer.profileStatus}.header`}/></h4>
      <IntlMessages
        id={`pages.customer.profile.status.${customer.profileStatus}.message`}>{toParagraphs}</IntlMessages>
    </div>
  </div>
}


const LocaleSetting = ({onUpdated, currentLocale}) => {
  const setLocale = async (element) => {
    const selectedLocale = element.target.form.elements.locale.value
    await updateLocaleSetting(selectedLocale)
    onUpdated()
  }


  const LocaleOption = ({label, value}) => <label className={cx('btn', 'btn-outline-info', {active: value === currentLocale})}>
    <input type="radio" name="locale" value={value} autoComplete="off"/> {label}
  </label>

  return <form className="text-center" onClick={setLocale}>
    <div className="btn-group btn-group-toggle pt-2" data-toggle="buttons">
      <LocaleOption label="English" value="en"/>
      <LocaleOption label="Dansk" value="da"/>
    </div>
  </form>
}

const CustomerDetails = ({intl, checkProfileStatus, settings}) => {
  const [Stripe, stripeLoadingError] = useStripe()
  return (
    <Query query={query} onCompleted={() => {
      checkProfileStatus()
    }}>{
      ({loading, error, data}) => {
        if (loading || !Stripe) return (<LoadingView/>)
        if (error) return (<Error error={error}/>)
        if (stripeLoadingError) return (<Error error={stripeLoadingError.message}/>)

        return <Mutation mutation={update}>
          {(updateCustomer) => {
            return <React.Fragment>
              <CardLayout columns={8}>
                <div className="card-header"><h4><IntlMessages
                  id={`pages.customer.profile.status.cardHeader`}/></h4></div>
                <div className="card-body">
                  <ProfileStatus customer={data.customer}/>
                </div>
              </CardLayout>
              <CardLayout columns={4}>
                <div className="card-header"><h4><IntlMessages
                  id={`pages.customer.profile.settings.cardHeader`}/></h4></div>
                <div className="card-body">
                  <h4 className=""><IntlMessages id="settings.locale"/></h4>
                  <LocaleSetting currentLocale={settings && settings.locale} onUpdated={checkProfileStatus} />
                </div>
              </CardLayout>
              <CardLayout columns={12}>
                <div className="card-body">
                  <Form schema={getSchema(intl, data.countryCodes, data.customer.billingMethod)}
                        formData={formatIncoming(data.customer)}
                        uiSchema={getUiSchema(intl, Stripe, data.customer.vatAndCountryLocked, false)}
                        fields={{layout_grid: LayoutField}}
                        onSubmit={data => updateCustomer({variables: data.formData})}
                  />
                </div>
              </CardLayout>
            </React.Fragment>
          }
          }
        </Mutation>
      }
    }
    </Query>
  )
}

const mapStateToProps = ({auth}) => {
  return {settings: auth.profile.settings}
}

export default connect(mapStateToProps, {checkProfileStatus})(injectIntl(CustomerDetails))




// WEBPACK FOOTER //
// ./Linkgua/components/CustomerDetails/index.js